import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { API_BASE } from '../utils/constants';
import { LoadingLogo } from './LoadingLogo';
import cantonLogo from '../canton-logo.png';

// Canton coin launch date (for determining if "Max" option should show)
const COIN_LAUNCH_DATE = new Date('2025-11-09');

const getTimeRanges = () => {
  const daysSinceLaunch = Math.floor((Date.now() - COIN_LAUNCH_DATE.getTime()) / (1000 * 60 * 60 * 24));

  const ranges = [
    { label: '24H', value: '1' },
    { label: '7D', value: '7' },
    { label: '1M', value: '30' },
    { label: '3M', value: '90' },
    { label: '1Y', value: '365' },
  ];

  // Only show "Max" if coin is less than 365 days old
  if (daysSinceLaunch < 365) {
    ranges.push({ label: 'Max', value: 'max' });
  }

  return ranges;
};

export function PriceChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRange, setSelectedRange] = useState('7');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [hoveredPrice, setHoveredPrice] = useState(null);

  // Handle resize for responsive layout
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPriceHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/price-history?days=${selectedRange}`);
        if (!response.ok) throw new Error('Failed to fetch price data');
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPriceHistory();
  }, [selectedRange]);

  const formatPrice = (price) => {
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(6)}`;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    if (selectedRange === '1') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (selectedRange === '7' || selectedRange === '30' || selectedRange === '90') {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' });
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `$${(volume / 1000000).toFixed(1)}M`;
    if (volume >= 1000) return `$${(volume / 1000).toFixed(1)}K`;
    return `$${volume.toFixed(0)}`;
  };

  const formatTooltipDate = (timestamp) => {
    const date = new Date(timestamp);
    if (selectedRange === '1') {
      return date.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isPositive = data?.stats?.priceChangePercent >= 0;

  // Calculate price reference line (starting price)
  const startPrice = useMemo(() => {
    if (!data?.prices?.length) return null;
    return data.prices[0].price;
  }, [data?.prices]);

  // Calculate total volume
  const totalVolume = useMemo(() => {
    if (!data?.prices?.length) return 0;
    return data.prices.reduce((sum, p) => sum + (p.volume || 0), 0);
  }, [data?.prices]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const priceFromStart = startPrice ? ((item.price - startPrice) / startPrice * 100) : 0;
      const isPriceUp = priceFromStart >= 0;

      return (
        <div style={{
          background: 'rgba(18, 20, 25, 0.95)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          padding: '14px 18px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(12px)',
          minWidth: '180px',
        }}>
          {/* Header with logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            paddingBottom: '10px',
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <img
              src={cantonLogo}
              alt="Canton"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
              }}
            />
            <span style={{
              color: 'var(--text-primary)',
              fontSize: '14px',
              fontWeight: '500',
            }}>
              Canton Coin
            </span>
          </div>

          {/* Date */}
          <div style={{
            color: 'var(--text-tertiary)',
            fontSize: '11px',
            marginBottom: '10px',
            letterSpacing: '0.5px',
          }}>
            {formatTooltipDate(item.timestamp)}
          </div>

          {/* Price */}
          <div style={{ marginBottom: '8px' }}>
            <div style={{
              color: 'var(--text-primary)',
              fontSize: '22px',
              fontWeight: '700',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '-0.5px',
            }}>
              {formatPrice(item.price)}
            </div>
            <div style={{
              fontSize: '12px',
              color: isPriceUp ? 'var(--accent)' : 'var(--negative)',
              fontWeight: '600',
              marginTop: '2px',
            }}>
              {isPriceUp ? '↑' : '↓'} {Math.abs(priceFromStart).toFixed(2)}% from start
            </div>
          </div>

          {/* Volume */}
          {item.volume > 0 && (
            <div style={{
              paddingTop: '8px',
              borderTop: '1px solid var(--border-subtle)',
            }}>
              <div style={{
                fontSize: '10px',
                color: 'var(--text-disabled)',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '2px',
              }}>Volume</div>
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'var(--font-mono)'
              }}>
                {formatVolume(item.volume)}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const displayPrice = hoveredPrice ?? data?.stats?.currentPrice;

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      borderRadius: '8px',
      border: '1px solid var(--border-default)',
      padding: isMobile ? '16px' : '24px',
      marginBottom: '24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle background grid pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        opacity: 0.5,
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          marginBottom: isMobile ? '20px' : '24px',
          gap: isMobile ? '16px' : '16px',
        }}>
          <div>
            {/* Token identifier */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '8px',
            }}>
              <img
                src={cantonLogo}
                alt="Canton"
                style={{
                  width: isMobile ? '28px' : '32px',
                  height: isMobile ? '28px' : '32px',
                  borderRadius: '50%',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: isMobile ? '14px' : '15px',
                  color: 'var(--text-primary)',
                  fontWeight: '500',
                }}>Canton Coin</span>
                <span style={{
                  background: 'var(--bg-subtle)',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: isMobile ? '10px' : '11px',
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: '500',
                }}>$CC</span>
              </div>
            </div>

            {/* Price display */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: isMobile ? '10px' : '14px',
              flexWrap: 'wrap',
            }}>
              <span style={{
                fontSize: isMobile ? '32px' : '42px',
                fontWeight: '700',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '-2px',
                lineHeight: 1,
              }}>
                {displayPrice ? formatPrice(displayPrice) : '—'}
              </span>
              {data?.stats && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: isPositive
                    ? 'rgba(0, 255, 136, 0.1)'
                    : 'rgba(255, 107, 107, 0.1)',
                }}>
                  <span style={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: isPositive ? 'var(--accent)' : 'var(--negative)',
                  }}>
                    {isPositive ? '▲' : '▼'}
                  </span>
                  <span style={{
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '700',
                    color: isPositive ? 'var(--accent)' : 'var(--negative)',
                    fontFamily: 'var(--font-mono)',
                  }}>
                    {isPositive ? '+' : ''}{data.stats.priceChangePercent.toFixed(2)}%
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Time Range Selector */}
          <div style={{
            display: 'flex',
            gap: '2px',
            background: 'var(--bg-subtle)',
            borderRadius: '8px',
            padding: '4px',
            alignSelf: isMobile ? 'flex-start' : 'center',
            border: '1px solid var(--border-subtle)',
          }}>
            {getTimeRanges().map((range) => (
              <button
                key={range.value}
                onClick={() => setSelectedRange(range.value)}
                style={{
                  padding: isMobile ? '8px 12px' : '8px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  background: selectedRange === range.value
                    ? 'var(--text-primary)'
                    : 'transparent',
                  color: selectedRange === range.value
                    ? 'var(--bg-primary)'
                    : 'var(--text-tertiary)',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontFamily: 'var(--font-mono)',
                }}
                onMouseEnter={(e) => {
                  if (selectedRange !== range.value) {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedRange !== range.value) {
                    e.currentTarget.style.color = 'var(--text-tertiary)';
                  }
                }}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div style={{
          height: isMobile ? '260px' : '320px',
          marginBottom: '16px',
          marginLeft: isMobile ? '-8px' : '-12px',
          marginRight: isMobile ? '-8px' : '-12px',
        }}>
          {loading ? (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <LoadingLogo size={48} />
            </div>
          ) : error ? (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--negative)',
              fontSize: '14px',
            }}>
              {error}
            </div>
          ) : data?.prices ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data.prices}
                margin={{ top: 20, right: 10, left: 0, bottom: 0 }}
                onMouseMove={(state) => {
                  if (state?.activePayload?.[0]) {
                    setHoveredPrice(state.activePayload[0].payload.price);
                  }
                }}
                onMouseLeave={() => setHoveredPrice(null)}
              >
                <defs>
                  {/* Price area gradient - refined */}
                  <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={isPositive ? '#00ff88' : '#ff6b6b'}
                      stopOpacity={0.15}
                    />
                    <stop
                      offset="50%"
                      stopColor={isPositive ? '#00ff88' : '#ff6b6b'}
                      stopOpacity={0.05}
                    />
                    <stop
                      offset="100%"
                      stopColor={isPositive ? '#00ff88' : '#ff6b6b'}
                      stopOpacity={0}
                    />
                  </linearGradient>
                  {/* Volume bars gradient */}
                  <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.15)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.03)" />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="timestamp"
                  tickFormatter={formatDate}
                  stroke="transparent"
                  tick={{ fill: 'var(--text-disabled)', fontSize: isMobile ? 9 : 10 }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={isMobile ? 50 : 60}
                  dy={8}
                />
                <YAxis
                  yAxisId="price"
                  domain={['auto', 'auto']}
                  tickFormatter={(val) => `$${val.toFixed(2)}`}
                  stroke="transparent"
                  tick={{ fill: 'var(--text-disabled)', fontSize: isMobile ? 9 : 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 50 : 60}
                  tickCount={5}
                />
                <YAxis
                  yAxisId="volume"
                  orientation="right"
                  domain={[0, (dataMax) => dataMax * 5]}
                  hide
                />

                {/* Reference line for starting price */}
                {startPrice && (
                  <ReferenceLine
                    yAxisId="price"
                    y={startPrice}
                    stroke="rgba(255,255,255,0.08)"
                    strokeDasharray="4 4"
                  />
                )}

                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{
                    stroke: 'rgba(255, 255, 255, 0.2)',
                    strokeWidth: 1,
                  }}
                />

                <Bar
                  yAxisId="volume"
                  dataKey="volume"
                  fill="url(#volumeGradient)"
                  radius={[1, 1, 0, 0]}
                />

                <Area
                  yAxisId="price"
                  type="monotone"
                  dataKey="price"
                  stroke={isPositive ? 'var(--accent)' : 'var(--negative)'}
                  strokeWidth={2}
                  fill="url(#priceGradient)"
                  animationDuration={800}
                  animationEasing="ease-out"
                  dot={false}
                  activeDot={{
                    r: 5,
                    fill: isPositive ? 'var(--accent)' : 'var(--negative)',
                    stroke: 'var(--bg-elevated)',
                    strokeWidth: 2,
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : null}
        </div>

        {/* Stats Bar */}
        {data?.stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: isMobile ? '12px' : '24px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* Range */}
            <div>
              <div style={{
                color: 'var(--text-disabled)',
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '6px',
              }}>
                {selectedRange === '1' ? '24h' : getTimeRanges().find(r => r.value === selectedRange)?.label} Range
              </div>
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'var(--font-mono)',
                fontWeight: '500',
              }}>
                {formatPrice(data.stats.low)} — {formatPrice(data.stats.high)}
              </div>
            </div>

            {/* Volume */}
            <div>
              <div style={{
                color: 'var(--text-disabled)',
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '6px',
              }}>
                {selectedRange === '1' ? '24h' : getTimeRanges().find(r => r.value === selectedRange)?.label} Volume
              </div>
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'var(--font-mono)',
                fontWeight: '500',
              }}>
                {formatVolume(totalVolume)}
              </div>
            </div>

            {/* High */}
            <div className={isMobile ? '' : ''}>
              <div style={{
                color: 'var(--text-disabled)',
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '6px',
              }}>
                Period High
              </div>
              <div style={{
                color: 'var(--accent)',
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'var(--font-mono)',
                fontWeight: '600',
              }}>
                {formatPrice(data.stats.high)}
              </div>
            </div>

            {/* Low */}
            <div>
              <div style={{
                color: 'var(--text-disabled)',
                fontSize: '10px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '6px',
              }}>
                Period Low
              </div>
              <div style={{
                color: 'var(--text-tertiary)',
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'var(--font-mono)',
                fontWeight: '600',
              }}>
                {formatPrice(data.stats.low)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
